import { Injectable } from '@angular/core';
import { Namespace } from 'enums-lib';
import { SortOption } from '../utils/fieldconfig';
import { FieldContext } from '../utils/fieldcontext';

interface ISortPathStrategy {
  getTemplateSortField(fieldContext: FieldContext, sortOption: SortOption): string | undefined;
  getModelSortFieldPath(fieldContext: FieldContext, sortOption: SortOption): string | undefined;
}

@Injectable({ providedIn: 'root' })
export class SortPathService {
  getTemplateSortField(fieldContext: FieldContext, sortOption: SortOption): string | undefined {
    return this.getStrategy(fieldContext).getTemplateSortField(fieldContext, sortOption);
  }

  getModelSortFieldPath(fieldContext: FieldContext, sortOption: SortOption): string | undefined {
    return this.getStrategy(fieldContext).getModelSortFieldPath(fieldContext, sortOption);
  }

  private getStrategy(fieldContext: FieldContext): ISortPathStrategy {
    if (fieldContext.namespace === Namespace.salesforce) {
      return SalesforceSortPathStrategy.getInstance();
    }

    return DefaultSortPathStrategy.getInstance();
  }
}

class DefaultSortPathStrategy implements ISortPathStrategy {
  private static instance: DefaultSortPathStrategy;

  static getInstance(): DefaultSortPathStrategy {
    if (!DefaultSortPathStrategy.instance) {
      DefaultSortPathStrategy.instance = new DefaultSortPathStrategy();
    }
    return DefaultSortPathStrategy.instance;
  }

  getTemplateSortField(fieldContext: FieldContext, sortOption: SortOption): string | undefined {
    if (!sortOption) {
      return undefined;
    }

    if (sortOption.childFieldPath) {
      return sortOption.childFieldPath;
    }

    return this.getLeaf(sortOption.fieldPath);
  }

  getModelSortFieldPath(fieldContext: FieldContext, sortOption: SortOption): string | undefined {
    return sortOption.fieldPath;
  }

  private getLeaf(path: string | undefined): string | undefined {
    if (!path) {
      return undefined;
    }
    return path.split('.').slice(-1)[0];
  }
}

class SalesforceSortPathStrategy implements ISortPathStrategy {
  private static instance: SalesforceSortPathStrategy;

  static getInstance(): SalesforceSortPathStrategy {
    if (!SalesforceSortPathStrategy.instance) {
      SalesforceSortPathStrategy.instance = new SalesforceSortPathStrategy();
    }
    return SalesforceSortPathStrategy.instance;
  }

  getTemplateSortField(fieldContext: FieldContext, sortOption: SortOption): string | undefined {
    const base = sortOption.childFieldPath ?? this.getLeaf(sortOption.fieldPath);
    return base ? this.escapeDoubleUnderscores(base) : base;
  }

  getModelSortFieldPath(fieldContext: FieldContext, sortOption: SortOption): string | undefined {
    const fieldPath = sortOption.fieldPath;
    if (!fieldPath) {
      return undefined;
    }

    const withRecords = this.ensureRecordsHop(fieldContext.field.path, fieldPath);
    return this.escapeDoubleUnderscores(withRecords);
  }

  private getLeaf(path: string | undefined): string | undefined {
    if (!path) {
      return undefined;
    }
    return path.split('.').slice(-1)[0];
  }

  private ensureRecordsHop(containerPath: string, path: string): string {
    if (!path.startsWith(containerPath)) {
      return path;
    }

    if (path.includes('.records.')) {
      return path;
    }

    const remainder = path.substring(containerPath.length);
    const suffix = remainder.startsWith('.') ? remainder : remainder ? `.${remainder}` : '';
    return `${containerPath}.records${suffix}`;
  }

  private escapeDoubleUnderscores(path: string): string {
    return path
      .split('.')
      .map((segment) => segment.replace(/__/g, '\\_\\_'))
      .join('.');
  }
}
